import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import './super-admin.css';
import { API_BASE, authHeaders } from '../config/api';
import { Button } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  UserOutlined,
  GlobalOutlined,
  CodeOutlined,
  FileTextOutlined,
  SettingOutlined,
  ProfileOutlined,
  QuestionCircleOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
  DollarOutlined,
  ShopOutlined,
  TeamOutlined,
  HighlightOutlined,
  LogoutOutlined,
  RobotOutlined,
  ApiOutlined,
} from '@ant-design/icons';

const SASidebar = () => {
  const navigate = useNavigate();

  const handleLogout = () => {
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      sessionStorage.clear();
    } catch {}
    navigate('/', { replace: true });
  };

  const [collapsed, setCollapsed] = useState(false);
  const [open, setOpen] = useState({
    whiteLabel: false,
  });

  // Get user info from localStorage and fetch avatar from API if needed
  const [user, setUser] = useState(null);
  useEffect(() => {
    const loadUser = async () => {
      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          const parsed = JSON.parse(stored);
          setUser(parsed);
          
          // If no avatar_url in localStorage, fetch from API
          if (!parsed.avatar_url) {
            try {
              const res = await fetch(`${API_BASE}/me`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
              if (res.ok) {
                const data = await res.json();
                if (data.avatar_url) {
                  const updated = { ...parsed, avatar_url: data.avatar_url };
                  localStorage.setItem('user', JSON.stringify(updated));
                  setUser(updated);
                }
              }
            } catch {}
          }
        }
      } catch {}
    };
    loadUser();
    
    const handleStorage = (e) => { if (e.key === 'user') loadUser(); };
    window.addEventListener('storage', handleStorage);
    const handleUserUpdate = () => loadUser();
    window.addEventListener('userUpdated', handleUserUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  const toggle = (key) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const width = collapsed ? 72 : 240;
  const label = (text) => collapsed ? null : <span style={{ marginLeft: 10, fontSize: 13 }}>{text}</span>;

  return (
    <aside className="sa-sidebar" style={{ width, transition: 'width .2s ease' }}>
      {/* Logo Section */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        padding: '8px 0 8px',
        marginBottom: 4,
        borderBottom: '1px solid rgba(148,163,184,0.15)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/dashboard/super-admin')}>
          <img 
            src="/headerlogo.png" 
            alt="Asuma Logo" 
            style={{ 
              height: collapsed ? '24px' : '28px', 
              width: 'auto',
              transition: 'height 0.2s ease'
            }} 
          />
          {!collapsed && (
            <div style={{ marginLeft: 0 }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '700', 
                color: '#fff',
                lineHeight: 1.2
              }}>Asuma</div>
              <div style={{ 
                fontSize: '9px', 
                fontWeight: '400', 
                color: '#94a3b8',
                fontStyle: 'italic',
                marginTop: -2
              }}>Super Admin</div>
            </div>
          )}
        </div>
        <Button
          type="text"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setCollapsed(c => !c)}
          size="small"
          style={{ color: '#fff', padding: '4px' }}
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        />
      </div>
      <nav>
        <ul>
          <li>
            <NavLink to="/dashboard/super-admin" end className={({isActive}) => isActive ? 'active' : undefined} title="Dashboard">
              <DashboardOutlined />{label('Dashboard')}
            </NavLink>
          </li>

          <li className="sa-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/super-admin/users" className={({isActive}) => isActive ? 'active' : undefined} title="Users Management">
              <UserOutlined />{label('Users Management')}
            </NavLink>
          </li>

          <li className="sa-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/super-admin/websites" className={({isActive}) => isActive ? 'active' : undefined} title="Websites Management">
              <GlobalOutlined />{label('Websites Management')}
            </NavLink>
          </li>

          <li className="sa-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/super-admin/snippets" className={({isActive}) => isActive ? 'active' : undefined} title="Snippets Management">
              <CodeOutlined />{label('Snippets Management')}
            </NavLink>
          </li>

          <li className="sa-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/super-admin/revenue" className={({isActive}) => isActive ? 'active' : undefined} title="Revenue & Earnings">
              <DollarOutlined />{label('Revenue & Earnings')}
            </NavLink>
          </li>

          <li className="sa-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/super-admin/logs" className={({isActive}) => isActive ? 'active' : undefined} title="Logs & Actions">
              <FileTextOutlined />{label('Logs & Actions')}
            </NavLink>
          </li>

          <li className="sa-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/super-admin/ai-pipeline" className={({isActive}) => isActive ? 'active' : undefined} title="AI Pipeline">
              <RobotOutlined />{label('AI Pipeline')}
            </NavLink>
          </li>

          <li className="sa-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/super-admin/api-portal" className={({isActive}) => isActive ? 'active' : undefined} title="API Portal">
              <ApiOutlined />{label('API Portal')}
            </NavLink>
          </li>

          <li className="sa-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/super-admin/settings" className={({isActive}) => isActive ? 'active' : undefined} title="Platform Settings">
              <SettingOutlined />{label('Platform Settings')}
            </NavLink>
          </li>

          <li className="sa-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/super-admin/profile" className={({isActive}) => isActive ? 'active' : undefined} title="Profile">
              <ProfileOutlined />{label('Profile')}
            </NavLink>
          </li>

          <li className="sa-divider" aria-hidden="true" />

          <li>
            <button
              type="button"
              className={`sa-section-toggle ${open.whiteLabel ? 'open' : ''}`}
              onClick={() => toggle('whiteLabel')}
              title="White Label"
            >
              {open.whiteLabel ? <CaretDownOutlined /> : <CaretRightOutlined />}
              {label('White Label')}
            </button>
            <div
              className={`sa-submenu ${open.whiteLabel ? 'open' : ''}`}
              style={{ maxHeight: open.whiteLabel && !collapsed ? 500 : 0 }}
            >
              <NavLink
                to="white-label"
                className={({ isActive }) => (isActive ? 'active' : undefined)}
                title="White Label Dashboard"
              >
                <ShopOutlined />
                {label('Overview')}
              </NavLink>
              <NavLink
                to="white-label/clients"
                className={({ isActive }) => (isActive ? 'active' : undefined)}
                title="Partner Companies"
              >
                <TeamOutlined />
                {label('Partner Companies')}
              </NavLink>
              <NavLink
                to="white-label/branding"
                className={({ isActive }) => (isActive ? 'active' : undefined)}
                title="Branding & Experience"
              >
                <HighlightOutlined />
                {label('Branding & Experience')}
              </NavLink>
              
            </div>
          </li>

          <li className="sa-divider" aria-hidden="true" />

          <li>
            <NavLink to="/dashboard/super-admin/support" className={({isActive}) => isActive ? 'active' : undefined} title="Support">
              <QuestionCircleOutlined />{label('Support')}
            </NavLink>
          </li>

          <li className="sa-divider" aria-hidden="true" />
          {/* Logout */}
          <li>
            <button onClick={handleLogout} title="Logout">
              <LogoutOutlined />{label('Logout')}
            </button>
          </li>
        </ul>
      </nav>

      {/* User Profile Section */}
      {!collapsed && user && (
        <div style={{ padding: '12px 8px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ 
              width: 36, 
              height: 36, 
              borderRadius: '50%', 
              background: '#1890ff', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0
            }}>
              {user.avatar_url ? (
                <img src={`${API_BASE}${user.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <UserOutlined style={{ color: '#fff', fontSize: 18 }} />
              )}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.name || user.email?.split('@')[0] || 'User'}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.email || ''}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version & Copyright */}
      {!collapsed && (
        <div style={{ padding: '8px 8px 10px', borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 4 }}>
          <div style={{ fontSize: 10, color: 'rgba(148,163,184,0.7)', textAlign: 'center', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600, letterSpacing: '0.04em' }}>v2.4.15</div>
            <div>© {new Date().getFullYear()} Asuma Analytics</div>
            <div style={{ fontSize: 9, opacity: 0.7 }}>All rights reserved</div>
          </div>
        </div>
      )}
      {collapsed && (
        <div style={{ padding: '8px 0', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 4 }}>
          <div style={{ fontSize: 9, color: 'rgba(148,163,184,0.6)' }}>v2.4.15</div>
        </div>
      )}

    </aside>
  );
};

export default SASidebar;
